import React from 'react';
import { cn } from '../../lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

export type VacationSummaryAccent = 'neutral' | 'primary' | 'warning';

/** A single coloured slice of the segmented allowance bar. */
export interface VacationSummarySegment {
  /** Portion of the allowance this slice represents, 0–100. */
  percent: number;
  /** Tailwind background colour class for the slice. */
  className: string;
  /** Stable key for the slice. */
  key: string;
}

/** A single row of the colour-coded legend rendered under the bar. */
export interface VacationSummaryLegendItem {
  /** Stable key for the row. */
  key: string;
  /** Label shown next to the colour dot. */
  label: string;
  /** Formatted value shown on the right (e.g. "1 day"). */
  value: string;
  /** Tailwind background colour class for the dot. */
  dotClassName: string;
}

interface VacationSummaryCardProps {
  label: string;
  value: number | string;
  hint?: string;
  icon?: React.ReactNode;
  /** When provided, renders a progress bar (0–100) below the value. */
  progress?: number;
  /**
   * When provided, renders a stacked/segmented bar (each slice a portion of the
   * allowance) instead of the single {@link progress} bar. Takes precedence.
   */
  segments?: VacationSummarySegment[];
  /** Small caption rendered under the bar (e.g. a legend of the segments). */
  caption?: string;
  /** Muted suffix rendered next to the value (e.g. "/ 20 days"). */
  valueSuffix?: string;
  /** Colour-coded legend rendered under the bar. */
  legend?: VacationSummaryLegendItem[];
  /** Description shown on hover over the bar. */
  barTooltip?: React.ReactNode;
  accent?: VacationSummaryAccent;
  /** Optional override for the value text colour (e.g. to highlight a count). */
  valueClassName?: string;
  testId?: string;
}

const iconAccent: Record<VacationSummaryAccent, string> = {
  neutral: 'text-muted-foreground',
  primary: 'text-prosigliere-blue',
  warning: 'text-orange-500',
};

/**
 * Single reusable summary tile used across the vacation overview
 * (Available / Used / Pending). Layout and behaviour are identical for every
 * tile; the `accent`, `icon`, `progress`/`segments` and copy props drive the
 * differences.
 */
export const VacationSummaryCard: React.FC<VacationSummaryCardProps> = ({
  label,
  value,
  hint,
  icon,
  progress,
  segments,
  caption,
  valueSuffix,
  legend,
  barTooltip,
  accent = 'neutral',
  valueClassName,
  testId,
}) => {
  const hasBar = segments !== undefined || progress !== undefined;

  const bar =
    segments !== undefined ? (
      <div
        className="flex h-1.5 w-full overflow-hidden rounded-full bg-muted"
        data-testid={testId ? `${testId}-progress` : undefined}
      >
        {segments.map((segment) => (
          <div
            key={segment.key}
            className={cn('h-full transition-all', segment.className)}
            style={{ width: `${Math.min(100, Math.max(0, segment.percent))}%` }}
          />
        ))}
      </div>
    ) : (
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
        data-testid={testId ? `${testId}-progress` : undefined}
      >
        <div
          className="h-full rounded-full bg-orange-500 transition-all"
          style={{ width: `${Math.min(100, Math.max(0, progress ?? 0))}%` }}
        />
      </div>
    );

  return (
    <div
      className="flex flex-col rounded-xl border border-border/60 bg-card p-5 shadow-sm"
      data-testid={testId}
    >
      <div className={cn('flex items-center gap-2 text-sm font-medium', iconAccent[accent])}>
        {icon && <span className="[&>svg]:h-4 [&>svg]:w-4">{icon}</span>}
        <span className="text-muted-foreground">{label}</span>
      </div>
      <p
        className={cn('mt-2 text-3xl font-bold tracking-tight text-foreground', valueClassName)}
        data-testid={testId ? `${testId}-value` : undefined}
      >
        {value}
        {valueSuffix && (
          <span className="ml-1 text-base font-medium text-muted-foreground">{valueSuffix}</span>
        )}
      </p>
      {hint && <p className="mt-1 text-sm text-muted-foreground">{hint}</p>}
      {hasBar &&
        (barTooltip ? (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="mt-4 cursor-help" data-testid={testId ? `${testId}-bar` : undefined}>
                  {bar}
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">{barTooltip}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <div className="mt-4">{bar}</div>
        ))}
      {caption && <p className="mt-2 text-xs text-muted-foreground">{caption}</p>}
      {legend && legend.length > 0 && (
        <ul
          className="mt-3 space-y-1.5"
          data-testid={testId ? `${testId}-legend` : undefined}
        >
          {legend.map((item) => (
            <li key={item.key} className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-muted-foreground">
                <span className={cn('h-2 w-2 rounded-full', item.dotClassName)} />
                {item.label}
              </span>
              <span className="font-medium text-foreground">{item.value}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
